import { ModalBody, ModalFooter, ModalHeader } from "@nextui-org/react";
import styles from "./PreconfirmUnification.module.scss";
import { colors } from "../../../../styles/_variables";
import Checkbox from "../../../Checkbox/Checkbox";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { useState } from "react";
import Alert from "../../../Alert/Alert";
import Button from "../../../Button/Button";
import { updateAction } from "../../../../store/slices/unifiedBalanceSlice";
import { approveUnification } from "../../../../connectors/unifiedBalanceApi";
import { useLocation } from "react-router-dom";
import { addMessage } from "../../../../utils/addMessage";
import { genericErrorMessage } from "../../../../utils";
import { useQueryClient } from "@tanstack/react-query";
import useScreenSize from "../../../../hooks/useScreenSize";

const PreconfirmUnification = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const { dataContainer, checkboxText, alertText, checkboxStyle, subtitle, modalHeader, modalFooterMobile, modalMobileHeader } =
    styles;
  const { isMobile } = useScreenSize();

  const {
    unifiedBalance: {
      selectedAccount,
      assessmentData: { assessment },
      allariaMasSelectedAccount,
    },
    authentication: { user },
  } = useAppSelector((state) => state);

  const email = queryParams.get("email") || user?.email || "";

  const [acceptedTerms, setAcceptedTerms] = useState<"YES" | "NO">("NO");
  const [loading, setLoading] = useState(false);

  const getAlertText = () => {
    switch (assessment.required_action) {
      case "ASSIGN_ROLE_COLLABORATOR":
        return "Vamos a agregarte como usuario a la cuenta de Allaria+ con el mismo mail.";
      case "CREATE_USER_IN_ALLARIA_MAS_WITH_COLLABORATOR_ROLE":
        return "Vamos a crearte un usuario en Allaria+, cuando finalice el proceso podrás generar una contraseña para ingresar a la plataforma.";
      default:
        return "";
    }
  };

  const handleUnification = async () => {
    setLoading(true);
    try {
      await approveUnification({
        email,
        accepted_tos: acceptedTerms === "YES",
        allaria_account_number: selectedAccount.number,
        allaria_mas_account_id: String(
          allariaMasSelectedAccount?.allaria_mas_account_id
        ),
      });

      dispatch(updateAction("FEEDBACK_SUCCESS"));

      // Invalidate the queries to trigger a refetch
      queryClient.invalidateQueries({
        queryKey: ["assessmentAccounts", email],
      });
      queryClient.invalidateQueries({
        queryKey: ["assessmentAccountsModal", email],
      });
    } catch (error) {
      addMessage({
        messageId: "allariaMasModal",
        message: genericErrorMessage,
        type: "error",
        position: "top-right",
      });
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ModalHeader className={modalMobileHeader}>
        {!isMobile ? (
          <h1
            style={{
              color: colors.primary[500],
              fontSize: "1.5rem",
              fontWeight: 600,
            }}
          >
            Unificar saldos
          </h1>
        ) : (
          <h1 className={modalHeader}>Unificar saldos</h1>
        )}
      </ModalHeader>
      <ModalBody>
        <p className={subtitle}>
          Es necesario aceptar los Términos y condiciones para finalizar con la
          unificación entre las cuentas:
        </p>

        <div className={dataContainer}>
          <p>Cuenta Comitente</p>
          <p>{selectedAccount.name.split("-")[0]}</p>
          <p>{selectedAccount.number}</p>
        </div>

        <div className={dataContainer}>
          <p>CVU</p>
          <p>{allariaMasSelectedAccount?.name}</p>
          <p>
            {allariaMasSelectedAccount?.alias} - CVU{" "}
            {allariaMasSelectedAccount?.cvu}
          </p>
        </div>

        <Checkbox
          classNames={acceptedTerms === "NO" ? { wrapper: checkboxStyle } : {}}
          value={acceptedTerms}
          isSelected={acceptedTerms === "YES"}
          onChange={() =>
            setAcceptedTerms(acceptedTerms === "YES" ? "NO" : "YES")
          }
        >
          <p className={checkboxText}>
            Acepto expresamente la unificación entre las cuentas
          </p>
        </Checkbox>

        {assessment.required_action !== "NOTIFY_ALLARIA_MAS" && (
          <Alert variant="INFO" className="mt-2">
            <p className={alertText}>{getAlertText()}</p>
          </Alert>
        )}
      </ModalBody>

      <ModalFooter
        className={!isMobile ? "flex justify-center items-center" : modalFooterMobile}
      >
        <div className={isMobile ? modalFooterMobile : "flex gap-4"}>
          <Button
            variant="secondary"
            text="Volver"
            onPress={() => {
              setAcceptedTerms("NO");
              dispatch(updateAction("ALLARIAMAS_SELECTOR"));
            }}
            className={isMobile ? "w-full mb-2.5" : ""}
          />
          <Button
            variant="primary"
            text="Continuar"
            isLoading={loading}
            isDisabled={acceptedTerms === "NO"}
            onPress={handleUnification}
            className={isMobile ? "w-full" : ""}
          />
        </div>
      </ModalFooter>
    </>
  );
};

export default PreconfirmUnification;
